import { useCallback, useEffect, useState } from "react";
import api from "api/api";
import Select from "react-select";

// mui components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

// mui icons
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// redux
import { useDispatch, useSelector } from "react-redux";

// componets
import { showNotification } from "store/features/notificationSlice";
import SanityCheckDialogue from "components/dataSeeding/SanityCheckDialogue";
import AlertDialogue from "components/dataSeeding/AlertDialogue";
import Loader from "../../common/Loader";

// props validation
import PropTypes from "prop-types";

function ImportMappingComponent({ studyTypeOptions = [], isDisabled = false }) {
    const dispatch = useDispatch();
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    const [selectedStudyType, setSelectedStudyType] = useState(null);
    const [selectedStudyTypeMethod, setSelectedStudyTypeMethod] = useState(null);
    const [studyTypeMethodOptions, setStudyTypeMethodOptions] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [loaderMessage, setLoaderMessage] = useState("Loading");

    const [title, setTitle] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [dataMappingFileId, setDataMappingFileId] = useState(null);

    const [errorMessage, setErrorMessage] = useState("");
    const [redSanityChecks, setRedSanityChecks] = useState([]);
    const [yellowSanityChecks, setYellowSanityChecks] = useState([]);

    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
    const [successDialogueOpen, setSuccessDialogueOpen] = useState(false);
    const [errorDialogueOpen, setErrorDialogueOpen] = useState(false);
    const [sanityCheckDialogOpen, setSanityCheckDialogOpen] = useState(false);

    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    const fetchStudyTypeMethods = useCallback(async () => {
        try {
            let url = process.env.REACT_APP_SERVER_PATH + `/study-type-methods/all/?product_category=${productCategoryId}`;
            if (selectedStudyType?.value) {
                url += `&study_type=${selectedStudyType.value}`;
            }

            await api
                .get(url)
                .then((res) => {
                    const options = res.data.map((method) => ({
                        value: method.id,
                        label: `${method.title} (${method.study_type_title})`,
                        originalLabel: method.title,
                        studyTypeLabel: method.study_type_title,
                        studyTypeValue: method.study_type,
                    }));
                    setStudyTypeMethodOptions(options);
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [handleError, productCategoryId, selectedStudyType]);

    useEffect(() => {
        fetchStudyTypeMethods();
    }, [fetchStudyTypeMethods]);

    const handleChange = (selectedValue, type) => {
        if (type === "studyType") {
            setSelectedStudyType(selectedValue);
            if (selectedValue && selectedStudyTypeMethod?.studyTypeValue !== selectedValue?.value) {
                setSelectedStudyTypeMethod(null);
            }
        } else {
            setSelectedStudyTypeMethod({
                value: selectedValue?.value,
                label: selectedValue?.originalLabel,
                studyTypeLabel: selectedValue?.studyTypeLabel,
                studyTypeValue: selectedValue?.studyTypeValue,
            });
        }
    };

    const handleFile = (e) => {
        if (e.target.files?.[0]) {
            setAttachment(e.target.files[0]);
            setTitle(e.target.files[0].name);
            e.target.value = "";
        }
    };

    const handleSanityCheckDialogue = () => {
        setSanityCheckDialogOpen(!sanityCheckDialogOpen);
    };

    const handleReset = () => {
        setSelectedStudyType(null);
        setSelectedStudyTypeMethod(null);
        setTitle("");
        setAttachment(null);
    };

    const handleSanityCheckDialogueGoBackButtonClick = async () => {
        const url = process.env.REACT_APP_SERVER_PATH + `/data-mapping-files/${dataMappingFileId}/`;
        const payload = {
            seeding_log: "Upload:User:Go Back",
        };
        try {
            await api
                .patch(url, payload)
                .then(async () => {})
                .catch((error) => {
                    console.error(error);
                });
        } catch (error) {
            console.error(error);
        }
    };

    const handleSuccessClose = () => {
        setSuccessDialogueOpen(false);
    };

    const handleErrorClose = () => {
        handleSanityCheckDialogueGoBackButtonClick();
        setErrorDialogueOpen(false);
    };

    const buildFormData = () => {
        const formData = new FormData();
        formData.append("attachment", attachment);
        formData.append("title", title);
        formData.append("product_category", productCategoryId);
        formData.append("study_type", selectedStudyType.value);
        formData.append("study_type_method", selectedStudyTypeMethod.value);

        return formData;
    };

    const handleSuccessResponse = async (data) => {
        setIsLoading(false);
        setIsImportDialogOpen(false);

        const hasRed = data.red?.length > 0;
        const hasYellow = data.yellow?.length > 0;

        if (hasRed) setRedSanityChecks(data.red);
        if (hasYellow) setYellowSanityChecks(data.yellow);

        if (hasRed && hasYellow) {
            setErrorMessage("Oh No! Validation Failed!");
            setErrorDialogueOpen(true);
        } else {
            setSuccessDialogueOpen(true);
        }

        try {
            await api.get(`${process.env.REACT_APP_SERVER_PATH}/crons/run/`);
        } catch (error) {
            console.error("Error", error);
        }
    };

    const handleErrorResponse = async (error) => {
        setIsLoading(false);
        setIsImportDialogOpen(false);
        console.error("Error", error);

        const err = error?.response?.data;

        if (err?.error) {
            if (err.error.red) setRedSanityChecks(err.error.red);
            if (err.error.yellow) setYellowSanityChecks(err.error.yellow);
            if (err.error.data_mapping_file_id?.length) setDataMappingFileId(err.error.data_mapping_file_id);

            setErrorMessage("Oh No! Validation Failed!");
            setErrorDialogueOpen(true);
            return;
        }

        if (err?.attachment) {
            handleError("error", err.attachment.join(" "));
            return;
        }

        if (err?.title) {
            handleError("error", err.title.join(" "));
            return;
        }

        handleError("error", error.message);
        setTitle("");
        setAttachment(null);
    };

    const handleSanitySuccessResponse = async (data) => {
        if (data?.red?.length) setRedSanityChecks([...data.red]);
        if (data?.yellow?.length) setYellowSanityChecks([...data.yellow]);

        const redExists = data?.red?.length;
        const yellowExists = data?.yellow?.length;

        if (redExists && yellowExists) {
            setErrorMessage("Oh No! Validation Failed!");
            setErrorDialogueOpen(true);
        } else {
            setSuccessDialogueOpen(true);
            setErrorDialogueOpen(false);
        }

        setSanityCheckDialogOpen(false);
        handleReset();

        try {
            await api.get(`${process.env.REACT_APP_SERVER_PATH}/crons/run/`);
        } catch (err) {
            console.error("error", err);
        }
    };

    const handleSanityErrorResponse = (error) => {
        const errData = error?.response?.data || {};

        if (errData.error) {
            if (errData.error?.red) setRedSanityChecks([...errData.error.red]);
            if (errData.error?.yellow) setYellowSanityChecks([...errData.error.yellow]);
            setErrorMessage("Oh No! Validation Failed!");
            setErrorDialogueOpen(true);
            return;
        }

        if (errData.attachment) {
            const str = errData.attachment.join(" ");
            handleError("error", str);
        } else if (errData.title) {
            const str = errData.title.join(" ");
            handleError("error", str);
        } else {
            handleError("error", error.message);
        }
    };

    const handleSanityCheckDialogueUpdateButtonClick = async () => {
        const url = `${process.env.REACT_APP_SERVER_PATH}/data-mapping-files/${dataMappingFileId}/`;
        const payload = {
            parsed: false,
            archive: false,
            seeding_log: "Upload:User:Validation Failed,Proceded by User",
        };

        setIsLoading(true);

        try {
            const res = await api.patch(url, payload);
            handleSanitySuccessResponse(res.data);
        } catch (error) {
            handleSanityErrorResponse(error);
        } finally {
            setIsLoading(false);
            setSanityCheckDialogOpen(false);
        }
    };

    const handleImport = async (e) => {
        if (!selectedStudyType || Object.keys(selectedStudyTypeMethod ?? {}).length === 0 || title === "") {
            handleError("error", "Please fill in all the fields.");
            return;
        }
        e.preventDefault();
        const url = `${process.env.REACT_APP_SERVER_PATH}/data-mapping-files/`;

        const formData = buildFormData();

        const config = {
            headers: {
                "content-type": "multipart/form-data",
            },
        };

        setLoaderMessage("Uploading");
        setIsLoading(true);

        try {
            const res = await api.post(url, formData, config);
            await handleSuccessResponse(res.data);
        } catch (error) {
            await handleErrorResponse(error);
        } finally {
            handleReset();
        }
    };

    const handleClose = () => {
        setIsImportDialogOpen(false);
        handleReset();
    };

    return (
        <>
            {isLoading && <Loader message={loaderMessage} />}
            <Button
                variant="outlined"
                startIcon={<ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                style={{
                    height: "30px",
                    minWidth: "auto",
                    padding: "6px 12px",
                    fontSize: "12px",
                    textTransform: "none",
                    border: isDisabled ? "1px solid rgba(0, 0, 0, 0.12)" : "1px solid #1976d2cc",
                }}
                onClick={() => setIsImportDialogOpen(true)}
                disabled={isDisabled}
            >
                Import
            </Button>
            {isImportDialogOpen && (
                <Dialog
                    aria-labelledby="customized-dialog-title"
                    open={isImportDialogOpen}
                    fullWidth={true}
                    sx={{
                        "& .MuiDialog-paper": {
                            maxWidth: "660px",
                            width: "100%",
                        },
                    }}
                >
                    <DialogTitle
                        style={{
                            backgroundColor: "rgb(51, 94, 234,0.1)",
                            color: "rgb(51, 94, 234)",
                            fontSize: 15,
                            textAlign: "center",
                            padding: "10px 12px",
                        }}
                    >
                        Import Data Mapping View:
                    </DialogTitle>
                    <DialogContent>
                        <div className="row pb-2 mt-4">
                            <div className="col-4">
                                <label
                                    htmlFor="studyType"
                                    className="input-label vertical-center text-sm "
                                >
                                    Study type :
                                </label>
                            </div>
                            <div className="col-8">
                                <Select
                                    id="studyType"
                                    className="text-sm input-label-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={studyTypeOptions}
                                    value={selectedStudyType}
                                    placeholder="Study Type"
                                    onChange={(newValue) => handleChange(newValue, "studyType")}
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            fontSize: "0.875rem",
                                            lineHeight: "1.25rem",
                                        }),
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </div>
                        <div className="row pb-2">
                            <div className="col-4">
                                <label
                                    htmlFor="studyTypeMethod"
                                    className="input-label vertical-center text-sm "
                                >
                                    Study Type Method :
                                </label>
                            </div>
                            <div className="col-8">
                                <Select
                                    id="studyTypeMethod"
                                    className="text-sm input-label-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={studyTypeMethodOptions}
                                    value={selectedStudyTypeMethod}
                                    placeholder="Study Type Method"
                                    onChange={(newValue) => handleChange(newValue, "studyTypeMethod")}
                                    styles={{
                                        menuPortal: (base) => ({
                                            ...base,
                                            zIndex: 9999,
                                            fontSize: "0.875rem",
                                            lineHeight: "1.25rem",
                                        }),
                                    }}
                                    menuPortalTarget={document.body}
                                />
                            </div>
                        </div>
                        <div className="row pb-2">
                            <div className="col-4">
                                <label
                                    htmlFor="customOutlineFile"
                                    className="input-label vertical-center text-sm "
                                >
                                    Select a file :
                                </label>
                            </div>
                            <div className="col-8">
                                <div className="custom-file pmd-custom-file-outline">
                                    <input
                                        onChange={handleFile}
                                        type="file"
                                        accept=".xlsx"
                                        className="custom-file-input"
                                        id="customOutlineFile"
                                        style={{
                                            height: "36px",
                                        }}
                                    />
                                    <style>
                                        {`
                                            .custom-file-label::after {
                                                height: 36px !important;
                                                line-height: 16px !important;
                                                font-size: 0.75rem
                                            }
                                        `}
                                    </style>
                                    <label
                                        className="custom-file-label"
                                        htmlFor="customOutlineFile"
                                        style={{
                                            height: "36px",
                                            lineHeight: "25px !important",
                                            fontSize: "0.875rem",
                                        }}
                                    >
                                        {title}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            paddingBottom: "20px",
                        }}
                    >
                        <div>
                            <Button
                                className="custom-btn mx-2"
                                variant="outlined"
                                size="small"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                className="custom-btn mx-2 px-4 liner-light-blue-background"
                                size="small"
                                color="primary"
                                autoFocus
                                variant="contained"
                                onClick={handleImport}
                            >
                                Upload
                            </Button>
                        </div>
                    </DialogActions>
                </Dialog>
            )}
            <AlertDialogue
                open={successDialogueOpen}
                icon="success"
                title="Success!"
                description="Your file has been uploaded successfully! &nbsp; Data seeding process has been triggered. It will take 30 minutes to reflect the data within the application."
                buttons={
                    <div>
                        <Button
                            size="small"
                            onClick={handleSuccessClose}
                            style={{
                                paddingLeft: 10,
                                paddingRight: 10,
                                textTransform: "unset",
                            }}
                            className="custom-btn-outlined color-blue"
                            variant="outlined"
                        >
                            Continue
                        </Button>
                    </div>
                }
            />
            <AlertDialogue
                open={errorDialogueOpen}
                icon="error"
                title="Error!"
                description={errorMessage}
                buttons={
                    <div>
                        <Button
                            size="small"
                            onClick={handleErrorClose}
                            style={{
                                paddingLeft: 10,
                                paddingRight: 10,
                                textTransform: "unset",
                            }}
                            className="custom-btn-outlined color-blue"
                            variant="outlined"
                        >
                            Go Back
                        </Button>
                        <Button
                            onClick={handleSanityCheckDialogue}
                            size="small"
                            style={{
                                paddingLeft: 10,
                                paddingRight: 10,
                                textTransform: "unset",
                                marginLeft: 10,
                            }}
                            className="custom-btn-outlined color-blue"
                            variant="outlined"
                        >
                            View Details
                        </Button>
                    </div>
                }
            />
            <SanityCheckDialogue
                open={sanityCheckDialogOpen}
                errors={[...redSanityChecks]}
                warnings={[...yellowSanityChecks]}
                errorMessage="Please resolve errors before moving ahead"
                title="Sanity Check"
                submitButton="Update"
                closeButton="Cancel"
                handleSubmit={handleSanityCheckDialogueUpdateButtonClick}
                handleClose={handleSanityCheckDialogue}
            />
        </>
    );
}

// PropTypes
ImportMappingComponent.propTypes = {
    studyTypeOptions: PropTypes.array,
    isDisabled: PropTypes.bool,
};

export default ImportMappingComponent;
